import React, { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { PiPlusBold, PiLockKeyBold, PiWarningBold } from "react-icons/pi";
import { SelectFilter, CountLabel, NoResults } from "../components/Bits";
import {
  STAFF, STAFF_ROLES, STAFF_ROLE_LABEL, STAFF_STATUS_META, ROLE_SCOPE,
} from "../data/mockData";

const ROLE_FILTERS = [
  { value: "all", label: "All roles" },
  ...STAFF_ROLES.map((r) => ({ value: r.value, label: r.label })),
];

/**
 * Admin Access — staff list and the invite flow.
 *
 * Not in the handoff design. Built strictly from components the system already
 * defines (.table, .tag, .card, .dialog, .input, .btn) so it reads as part of
 * the same product rather than a bolted-on screen. The invite dialog is a
 * two-step local flow: compose, then a sent confirmation carrying the
 * (fake) invite link — no request is made.
 */
const AdminAccess = () => {
  const navigate = useNavigate();
  const { search } = useOutletContext();
  const [roleFilter, setRoleFilter] = useState("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [sent, setSent] = useState(null);
  const [form, setForm] = useState({ email: "", role: "support", note: "" });
  const [error, setError] = useState("");

  const filtered = useMemo(
    () =>
      STAFF.filter((s) => {
        if (roleFilter !== "all" && s.role !== roleFilter) return false;
        if (search && !(s.name.toLowerCase().includes(search) || s.email.toLowerCase().includes(search))) return false;
        return true;
      }),
    [roleFilter, search]
  );

  const closeInvite = () => {
    setInviteOpen(false);
    setSent(null);
    setForm({ email: "", role: "support", note: "" });
    setError("");
  };

  const submitInvite = () => {
    const email = form.email.trim();
    if (!email) return setError("Enter an email address.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("That doesn't look like a valid email address.");
    if (STAFF.some((s) => s.email.toLowerCase() === email.toLowerCase())) {
      return setError("That address already has an admin account.");
    }
    setError("");
    setSent({ email, role: form.role });
  };

  const selectedRole = STAFF_ROLES.find((r) => r.value === form.role);

  return (
    <>
      <div className="admin-toolbar">
        <SelectFilter value={roleFilter} onChange={setRoleFilter} options={ROLE_FILTERS} minWidth={150} />
        <div className="admin-spacer" />
        <CountLabel shown={filtered.length} total={STAFF.length} noun="staff" />
        <button type="button" className="btn btn-primary" onClick={() => setInviteOpen(true)}>
          <PiPlusBold /> Invite staff
        </button>
      </div>

      <div className="card admin-table-card">
        <table className="table">
          <thead>
            <tr>
              <th className="admin-cell-lead">Name</th>
              <th>Role</th>
              <th>Two-factor</th>
              <th>Last active</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <NoResults colSpan={6} />}
            {filtered.map((s) => {
              const status = STAFF_STATUS_META[s.status];
              return (
                <tr key={s.id}>
                  <td className="admin-cell-lead">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className="admin-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                        {s.initials}
                      </div>
                      <div>
                        <div className="admin-cell-strong">{s.name}</div>
                        <div className="admin-cell-sub">{s.email}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="tag tag-neutral">{STAFF_ROLE_LABEL[s.role]}</span></td>
                  <td>
                    {s.mfa ? (
                      <span className="admin-status" style={{ color: "var(--status-ok)" }}>
                        <PiLockKeyBold /> Enabled
                      </span>
                    ) : (
                      <span className="admin-status" style={{ color: "var(--status-warn)" }}>
                        <PiWarningBold /> Not set up
                      </span>
                    )}
                  </td>
                  <td className="admin-muted-cell">{s.lastActive}</td>
                  <td><span className={`tag ${status.tagClass}`}>{status.label}</span></td>
                  <td className="admin-chevron">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ fontSize: 12 }}
                      onClick={() => navigate("/admin/onboarding")}
                      title="Preview the onboarding this staffer receives"
                    >
                      Preview onboarding
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {inviteOpen && (
        <div className="dialog-backdrop" onClick={closeInvite}>
          <div className="dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            {!sent ? (
              <>
                <div className="dialog-title">Invite a staff member</div>
                <div className="dialog-body">
                  They'll receive an email with a single-use link to set a password and enrol in
                  two-factor authentication before they can reach the dashboard.
                </div>

                <div className="admin-field-list">
                  <div className="field">
                    <label htmlFor="invite-email">Work email</label>
                    <input
                      id="invite-email"
                      className="input"
                      type="email"
                      placeholder="name@buildbeta.co.uk"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="invite-role">Role</label>
                    <select
                      id="invite-role"
                      className="input"
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                    >
                      {STAFF_ROLES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    <div className="card-meta" style={{ marginTop: 6 }}>{selectedRole.blurb}</div>
                  </div>

                  <div className="field">
                    <label htmlFor="invite-note">Note (optional)</label>
                    <textarea
                      id="invite-note"
                      className="input"
                      placeholder="Anything they should know before they start."
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                    />
                  </div>

                  <div>
                    <div className="admin-section-label">They will be able to reach</div>
                    <div className="admin-chips">
                      {ROLE_SCOPE[form.role].map((s) => (
                        <span className="tag tag-outline" key={s} style={{ fontSize: 10 }}>{s}</span>
                      ))}
                    </div>
                  </div>

                  {error && (
                    <div style={{ fontSize: 12, color: "var(--status-danger)" }}>{error}</div>
                  )}
                </div>

                <div className="dialog-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeInvite}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={submitInvite}>Send invitation</button>
                </div>
              </>
            ) : (
              <>
                <div className="dialog-title">Invitation sent</div>
                <div className="dialog-body">
                  <strong>{sent.email}</strong> has been invited as{" "}
                  <strong>{STAFF_ROLE_LABEL[sent.role]}</strong>. The link expires in 7 days.
                </div>
                <div className="admin-check" style={{ wordBreak: "break-all" }}>
                  https://admin.buildbeta.co.uk/onboarding?token=inv_8f2c41d9e7
                </div>
                <div className="dialog-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeInvite}>Done</button>
                  <button type="button" className="btn btn-primary" onClick={() => navigate("/admin/onboarding")}>
                    Preview their onboarding
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAccess;
